export type Theme = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'theme';

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    // If no stored theme, default to system
    if (!stored) {
      return 'system';
    }
    return stored as Theme;
  } catch {
    return 'system';
  }
}

export function setStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore localStorage errors
  }
}

export function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery.matches ? 'dark' : 'light';
}

export function getEffectiveTheme(): 'light' | 'dark' {
  const storedTheme = getStoredTheme();

  // If theme is set to 'system', always use system preference
  if (storedTheme === 'system') {
    return getSystemTheme();
  }

  // Otherwise use the stored theme
  return storedTheme;
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;

  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme;

  if (effectiveTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function initializeTheme(): void {
  const theme = getStoredTheme();
  applyTheme(theme);
}

export function watchSystemTheme(callback: (theme: 'light' | 'dark') => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handleChange = (_event: MediaQueryListEvent) => {
    callback(getSystemTheme());
  };

  mediaQuery.addEventListener('change', handleChange);

  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
}
